// MapInput.tsx

import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix marker icons
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type Mode = "form" | "display";

interface MapInputProps {
  mode?: Mode;
  mapHeight?: string | number;
  lat?: number | null;
  lon?: number | null;
  onMapEvent?: (coords: { lat: number; lon: number }) => void;

  /** NEW SETTINGS */
  enableZoom?: boolean;          // Enable zoom buttons + pinch
  enablePan?: boolean;           // Enable drag move
  enableScrollZoom?: boolean;    // Enable mouse wheel zoom
  enableTouchZoom?: boolean;     // Enable touch pinch zoom
}

const MapInput: React.FC<MapInputProps> = ({
  mode = "form",
  mapHeight = "400px",
  lat = 23.8103,
  lon = 90.4125,
  onMapEvent,

  enableZoom = true,
  enablePan = true,
  enableScrollZoom = true,
  enableTouchZoom = true,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const [latitude, setLatitude] = useState<number>(lat ?? 23.8103);
  const [longitude, setLongitude] = useState<number>(lon ?? 90.4125);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      zoomControl: enableZoom,
      scrollWheelZoom: enableScrollZoom,
      dragging: enablePan,
      touchZoom: enableTouchZoom,
      doubleClickZoom: enableZoom,
      attributionControl: false,
    }).setView([latitude, longitude], 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
      mapRef.current
    );

    setMarker(latitude, longitude, mode === "form");
  }, []);

  // Apply interaction settings dynamically when props change
  useEffect(() => {
    if (!mapRef.current) return;

    enableZoom ? mapRef.current.zoomControl?.addTo(mapRef.current) : mapRef.current.zoomControl?.remove();

    enableScrollZoom
      ? mapRef.current.scrollWheelZoom.enable()
      : mapRef.current.scrollWheelZoom.disable();

    enablePan
      ? mapRef.current.dragging.enable()
      : mapRef.current.dragging.disable();

    enableTouchZoom
      ? mapRef.current.touchZoom.enable()
      : mapRef.current.touchZoom.disable();
  }, [enableZoom, enablePan, enableScrollZoom, enableTouchZoom]);

  // Update marker when lat/lon props change
  useEffect(() => {
    if (!mapRef.current) return;
    setMarker(lat ?? 23.8103, lon ?? 90.4125, mode === "form");
  }, [lat, lon, mode]);

  const setMarker = (lat: number, lon: number, draggable: boolean) => {
    if (!mapRef.current) return;

    if (markerRef.current) markerRef.current.remove();

    markerRef.current = L.marker([lat, lon], { draggable }).addTo(
      mapRef.current
    );
    mapRef.current.setView([lat, lon], 13);

    setLatitude(lat);
    setLongitude(lon);
    onMapEvent?.({ lat, lon });

    if (draggable) {
      markerRef.current.on("dragend", () => {
        const pos = markerRef.current!.getLatLng();
        setLatitude(pos.lat);
        setLongitude(pos.lng);
        onMapEvent?.({ lat: pos.lat, lon: pos.lng });
      });
    }
  };

  return (
    <div>
      <div
        ref={mapContainerRef}
        style={{ height: mapHeight, width: "100%", borderRadius: "8px" }}
      ></div>
    </div>
  );
};

export default MapInput;
